import { useCallback, useMemo } from "react";
import { getCapabilities } from "@/api/comman/capabilitiesApi";
import { useToken, useUser } from "@/stores/authStore";
import { toCamelCase } from "@/utils/helpers";


const featureEntry = (features, key) => features?.[key] ?? features?.[toCamelCase(key)];

export default function usePermission() {
  const token = useToken();
  const user = useUser();
  const { data, isLoading } = getCapabilities(`${user?.userId ?? ""}-${user?.companyId ?? ""}`, {
    enabled: Boolean(token),
  });
  const capabilities = data?.data;

  const permissions = useMemo(() => new Set(capabilities?.permissions ?? []), [capabilities]);

  const hasPermission = useCallback(
    (permission) => {
      if (!permission || !permissions.has(permission)) return false;
      const state = featureEntry(capabilities?.features, permission)?.state;
      return state !== "hidden" && state !== "disabled";
    },
    [permissions, capabilities],
  );

  const hasAnyPermission = useCallback(
    (list = []) => list.some((permission) => hasPermission(permission)),
    [hasPermission],
  );

  const getLimit = useCallback(
    (key) => capabilities?.limits?.[key] ?? capabilities?.limits?.[toCamelCase(key)],
    [capabilities],
  );

  const getRemaining = useCallback(
    (key) => {
      const remaining = featureEntry(capabilities?.features, key)?.remaining;
      if (remaining !== undefined) return remaining;
      const limit = getLimit(key);
      return limit === undefined || limit === -1 ? undefined : limit;
    },
    [capabilities, getLimit],
  );

  return { hasPermission, hasAnyPermission, isLoading, capabilities, getLimit, getRemaining };
  return { hasPermission, hasAnyPermission, isLoading, capabilities };
}
